<template>
  <v-container class="notification-center" fluid>
    <drawer-dialog
      v-model="openSettingForm"
      :is-loading="false"
      width="70%"
    >
      <setting-form
        v-if="openSettingForm && triggers"
        :entity-id="project"
        :is-loading="false"
        :readonly="false"
        :triggers="triggers"
        :notification="currentSettingData ?? undefined"
        :has-mail-template-access="hasMailTemplateAccess"
        :has-bonus-tools-access="hasBonusToolsAccess"
        :mail-templates="mailTemplates"
        :benefit-options="benefitOptions"
        @add="loadProjectData()"
        @close="() => openSettingForm = false"
        @save="() => {}"
      >
        {{ isNewNotification ? 'add new notification' : 'edit notification' }}
      </setting-form>
    </drawer-dialog>
    <v-dialog
      v-model="isCopyingNotification"
      max-width="600"
    >
      <v-card>
        <v-card-title v-text="'Copy Notification'" />
        <v-card-text>
          <v-autocomplete
            v-model="copyTargetEntities"
            :items="getCopyTargetEntities()"
            label="Select projects to copy this notification for"
            :loading="loading"
            multiple
          />
        </v-card-text>
        <v-card-actions
          class="justify-end"
        >
          <v-btn
            v-text="'Cancel'"
            color="error"
            :loading="loading"
            @click="isCopyingNotification = false"
          />
          <v-btn
            v-text="'Apply'"
            color="primary"
            :disabled="!copyTargetEntities.length"
            :loading="loading"
            @click="onConfirmCopy"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <drawer-dialog
      v-model="isAuditLogDisplayed"
      width="70%"
      max-width="1200px"
    >
      <audit-log
        v-if="isAuditLogDisplayed"
        :audit-log="auditLog"
        :notification="auditLogTarget"
        :is-loading="isLoadingAuditLog"
        @close="isAuditLogDisplayed = false"
      />
    </drawer-dialog>

    <v-row class="mx-0" style="height:48px;">
      <h2 class="notification-center__title">
        Notifications Center
      </h2>
    </v-row>
    <v-row class="mx-0 pb-4">
      <select-project
        v-model="project"
        style="min-width: 250px; max-width: 250px"
      ></select-project>
      <v-btn :disabled="loading" :loading="loading" class="mt-3 ml-7" color="success" @click="addNewNotification">Add New Notification</v-btn>
      <div class="mt-3 ml-7 pt-2">
        <v-icon @click="loadProjectData()">mdi-reload</v-icon>
      </div>
      <v-spacer />
      <v-icon v-if="!isOpenSearchInput" @click="isOpenSearchInput = !isOpenSearchInput" class="mt-3 ml-7">mdi-magnify</v-icon>
      <v-text-field
        v-else-if="isOpenSearchInput"
        v-model="searchQueryByName"
        clearable
        hide-details="auto"
        label="Search by name"
        style="min-width: 220px; max-width: 220px"
      >
        <template #prepend-inner>
          <v-icon @click="isOpenSearchInput = !isOpenSearchInput">mdi-magnify</v-icon>
        </template>
      </v-text-field>
    </v-row>

    <div class="notification-center__wrapper">
      <v-data-iterator
        :custom-sort="sortByDate"
        :custom-filter="filterBy"
        :items="items"
        :loading="loading"
        :search="searchQueryByName"
        :sort-desc="sortBy"
        item-key="event_id"
        fixed-header
        style="height: calc(100vh - 150px)"
      >
        <template v-slot:header>
          <v-card class="px-4 notification-center__sort">
            <v-row class="pa-0 ma-0 notification-center__sort-wrapper">
              <div class="notification-center__sort-item">
                <span><b>Sort by:</b></span>
                <v-select
                  v-model="sortBy"
                  :dense="true"
                  :items="sortList"
                  auto-select-first
                  class="mt-0"
                  dense
                  hide-details
                  label=""
                  style="min-width: 150px; max-width: 150px"
                />
              </div>
              <div class="notification-center__sort-item">
                <span><b>Status:</b></span>
                <div class="notification-center__sort-item-group">
                  <v-chip-group
                    v-model="chosenStatus"
                    active-class="success--text"
                    class="notification-center__sort-item-group-chip"
                    multiple
                  >
                    <v-chip v-for="type in $options.STATUS" :key="type.value" :value="type.value" outlined>{{ type.text }}</v-chip>
                  </v-chip-group>
                </div>
              </div>
              <div>
                <div class="notification-center__sort-item">
                  <span><b>Type:</b></span>
                  <div class="notification-center__sort-item-group">
                    <v-chip-group
                      v-model="chosenTypes"
                      active-class="success--text"
                      class="notification-center__sort-item-group-chip"
                      multiple
                    >
                      <v-chip v-for="type in $options.NOTIFICATION_TYPES" :key="type.value" :value="type.value" outlined>{{ type.text }}</v-chip>
                    </v-chip-group>
                  </div>
                </div>
              </div>
              <div>
                <div class="notification-center__sort-item">
                  <span><b>Trigger: </b></span>
                  <div class="notification-center__sort-item-group">
                    <v-select
                      v-model="chosenTriggers"
                      :item-value="'value'"
                      :items="triggers"
                      clearable
                      hide-details
                      multiple
                      style="min-width: 330px; max-width: 330px"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index <= 0" color="success--text" outlined>
                          <span>{{ item.text }}</span>
                        </v-chip>
                        <span
                          v-if="index === 1"
                          class="grey--text text-caption"
                        >
                      (+{{ chosenTriggers.length - 1 }} others)
                     </span>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
            </v-row>
          </v-card>
        </template>
        <template v-slot:default="{ items }">
          <v-expansion-panels v-model="panel" accordion class="mt-4 d-flex notification-center__expansion" multiple>
            <v-expansion-panel
              v-for="(notification, indexNotification) in items"
              class="notification-center__expansion-panel"
              :key="notification.event_id"
              @click="openPanel(notification, indexNotification)"
            >
              <v-expansion-panel-header v-slot="{ open }" class="notification-center__expansion-panel-header">
                <v-row :align="'center'" class="d-flex order-2 justify-space-between ml-2 flex-nowrap" style="max-height: 100%;" no-gutters>
                  <div class="notification-center__expansion-panel-header-title"><span>{{ notification.name }}</span></div>
                  <div class="notification-center__expansion-panel-header-active">
                    <v-badge :color="notification.status === 'active' ? 'primary' : 'grey'" class="notification-center__expansion-panel-header-active-badge" inline></v-badge>
                    <span class="text-capitalize">{{ notification?.status || 'disabled' }}</span>
                    <v-switch
                      v-model="notification.status"
                      @change="(value) => updateStatusNotification(notification, value)"
                      @click.stop
                      true-value="active"
                      false-value="disabled"
                      hide-details
                      class="notification-center__expansion-panel-header-active-switch pa-0"
                    />
                  </div>
                  <div class="notification-center__expansion-panel-header-type d-flex align-center">
                    <div>
                      <v-icon>mdi-star-box-multiple-outline</v-icon>
                    </div>
                    <span class="notification-center__expansion-panel-header-type-text">Type:</span>
                    <span class="text-capitalize">{{ notification?.rules?.type }}</span>
                  </div>
                  <div v-if="notification.languages" @click.stop="() => {}" class="d-flex" style="z-index: 2">
                    <select-lang
                      :isOpen="notification.isOpen"
                      :isRounded="true"
                      :languages="notification.languages"
                      :value="notification.chosenLanguage"
                      @addLang="(e) => toggleCurrentTranslation(e, notification)"
                      @open="(e) => openSelectFrame(e, {notification, indexNotification})"
                    />
                    <lang-row
                      class="pl-4"
                      :countries="notification.languages"
                    />
                  </div>

                  <div class="notification-center__expansion-panel-header-date d-flex align-center">
                    <span>Created at:</span>
                    <span>{{ notification.created }}</span>
                  </div>
                  <div class="notification-center__expansion-panel-header-date">
                    <span>Updated at:</span>
                    <span>{{ notification.updated }}</span>
                    <span>By:</span>
                    <span>{{ notification.modified_by }}</span>
                  </div>

                  <div class="notification-center__expansion-panel-header-btns d-flex align-center" style="gap: 0.75rem;">
                    <v-btn
                      color="primary"
                      icon
                      outlined
                      small
                      :loading="isLoadingAuditLog"
                      title="Audit Log"
                      @click.stop="onLoadAuditLog(notification)">
                      <v-icon v-text="'mdi-state-machine'" small />
                    </v-btn>
                    <v-btn
                      color="primary"
                      icon
                      outlined
                      small
                      :loading="isLoadingEntities || loading"
                      title="Copy"
                      @click.stop="onCopyNotification(notification)"
                    >
                      <v-icon v-text="'mdi-file'" small />
                    </v-btn>
                    <v-btn
                      v-text="'Edit'"
                      class="notification-center__expansion-panel-header-btn"
                      color="success"
                      outlined
                      @click.stop="editNotification(notification)"
                    />
                  </div>

                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-divider></v-divider>
                <div class="notification-center__expansion-panel-wrapper-content">
                  <div class="notification-center__expansion-panel-content">
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <span>Header</span>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.base?.[notification.chosenLanguage]?.header)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <span>Content</span>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.base?.[notification.chosenLanguage]?.content)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <div>Excerpt</div>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.base?.[notification.chosenLanguage]?.excerpt)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <div>Toast excerpt</div>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.toast?.[notification.chosenLanguage]?.excerpt)">
                      </div>
                    </section>
                  </div>
                  <div class="notification-center__expansion-panel-content">
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <div>Popup header</div>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.popup?.[notification.chosenLanguage]?.header)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <div>Popup content</div>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.popup?.[notification.chosenLanguage]?.content)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <div>Popup excerpt</div>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field" v-html="$sanitize(notification.template.popup?.[notification.chosenLanguage]?.excerpt)">
                      </div>
                    </section>
                    <section class="notification-center__expansion-panel-content-section">
                      <div class="notification-center__expansion-panel-content-section-title">
                        <span>URL</span>
                      </div>
                      <div class="notification-center__expansion-panel-content-section-field">
                        <a v-html="$sanitize(`${notification.template?.url[notification.chosenLanguage]?.header }: ${notification.template.url?.[notification.chosenLanguage]?.content}`)"></a>
                      </div>
                    </section>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

        </template>
      </v-data-iterator>
    </div>
  </v-container>
</template>

<script>
  import { map } from 'lodash'
  import { API } from '@/utils/api'
  import SelectLang from '@/components/core/SelectLang/SelectLang.vue'
  import { localeToIsoPrefix } from '@/components/core/SelectLang/mapperIsoLocale'
  import { DrawerDialog } from '../components/core/DrawerDialog'
  import { filters } from '@/utils/filters'
  import SettingForm from '@/components/notification-center/setting-form.vue'
  import AuditLog from '@/components/notification-center/NotificationEventAuditLogForm.vue'
  import { NOTIFICATION_TYPES, STATUS } from '@/components/notification-center/const'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
  import LangRow from '@/components/core/SelectLang/LangRow/LangRow.vue'

  const ENTITY_LIST = '/users/entity_list.json'

  export default {
    NOTIFICATION_TYPES,
    STATUS,
    components: {
      SelectProject,
      LangRow,
      SettingForm,
      DrawerDialog,
      SelectLang,
      AuditLog,
    },

    data: () => ({
      panel: [],

      project: null,
      loading: false,
      openSettingForm: false,
      items: [],
      pagination: {},
      mailTemplates: [],
      benefitOptions: [],
      openSelect: false,
      currentSettingData: null,
      isNewNotification: false,
      isOpenSearchInput: false,
      searchQueryByName: '',

      sortBy: false,
      sortList: [
        { text: 'Newest First', value: false },
        { text: 'Oldest First', value: true }
      ],

      chosenTypes: [],
      chosenStatus: [ 'active' ],
      chosenTriggers: [],
      triggers: [],
      entities: [],
      isLoadingEntities: false,
      isCopyingNotification: false,
      notificationToCopy: null,
      copyTargetEntities: [],

      auditLog: [],
      isLoadingAuditLog: false,
      auditLogTarget: null,
    }),

    computed: {
      projectList: function () {
        return map(this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id }))
      },
      filterSortOptions () {
        return {
          trigger: { data: this.chosenTriggers, type: 'combobox' },
          'rules.type': { data: this.chosenTypes, type: 'combobox' },
          status: { data: this.chosenStatus, type: 'combobox' },
          name: { data: this.searchQueryByName, type: 'text' }
        }
      },
      filterBy () {
        return (items, search) => filters(items, this.filterSortOptions)
          .sort((a, b) => this.sortBy
            ? new Date(a.created) - new Date(b.created)
            : new Date(b.created) - new Date(a.created)
          )
      },
      hasMailTemplateAccess () {
        return this.$store.state.auth.checkAccess({ key: 'mail_template', value: true })
      },
      hasBonusToolsAccess () {
        return this.$store.state.auth.checkAccess({ key: 'bonus_tools', value: true })
      },

      isAuditLogDisplayed: {
        get() {
          return !!this.auditLogTarget
        },

        set(value) {
          if (!value) {
            this.auditLogTarget = null
          }
        }
      }
    },

    mounted () {
      this.project = this.projectList[0].value
      this.loadAvailableProjects()
    },

    watch: {
      openSettingForm () {
        if (!this.openSettingForm) {
          this.currentSettingData = null
        }
      },
      project () {
        this.loadProjectData(true)
      },
      sortBy () {
        this.resetStateOpens()
      },
      filterSortOptions: {
        handler (option) {
          this.resetStateOpens()
        },
        immediate: true,
        deep: true
      },
      isCopyingNotification (newValue) {
        if (!newValue) {
          this.notificationToCopy = null
          this.copyTargetEntities = []
        }
      }
    },

    methods: {
      resetStateOpens () {
        this.panel = []
        this.items = this.items.map(item => ({ ...item, isOpen: false }))
      },
      updateStatusNotification (notification, status) {
        API.call('/$v/notification-center/events/update.json', {
          event: {
            status,
            event_id: notification.event_id,
            entity_id: notification.entity_id,
          }
        })
      },
      openPanel (notification, indexNotification) {
        if (this.panel.includes(indexNotification) && notification.isOpen) {
          notification.isOpen = !notification.isOpen
        }
      },
      localeToIsoPrefix,
      sortByDate (filteredItems, _, [ sortDesc ]) {
        return filteredItems.sort((a, b) => sortDesc
          ? new Date(a.created) - new Date(b.created)
          : new Date(b.created) - new Date(a.created)
        )
      },
      addNewNotification () {
        this.currentSettingData = null
        this.isNewNotification = true
        this.openSettingForm = true
      },
      editNotification (notification) {
        this.currentSettingData = notification
        this.isNewNotification = false
        this.openSettingForm = true
      },
      openSelectFrame (event, { notification, indexNotification }) {
        if (!this.panel.includes(indexNotification) && event) {
          this.panel = [ ...this.panel, indexNotification ]
        }
        notification.isOpen = event
      },

      toggleCurrentTranslation (e, notification) {
        notification.chosenLanguage = e
      },

      async loadEvents () {
        this.items = []
        const [ triggers, items ] = await Promise.all([
          API.call('/$v/notification-center/events/trigger_list.json', {
            entity_id: this.project
          }),
          API.call('/$v/notification-center/events/list.json', {
            entity_id: this.project
          })
        ])

        this.triggers = triggers.data
        this.items = map(items.data, (o) => {
          return o
        })
        this.pagination = triggers.pagination
      },

      async loadMailTemplates () {
        const mailTemplates = await API.call('/mail/template/list.json', {
          entity_id: this.project
        })

        this.mailTemplates = mailTemplates.data
      },

      loadBenefitOptions() {
          return API.call('/bonus_tools/dictionary/benefit_list.json', {}, null, this.project).then(r => {
            this.benefitOptions = r.data
          }).catch(e => {
          }).finally(() => {
            this.respin.benefitListLoading = false
          })
      },

      async loadProjectData (withMailTemplates = false) {
        this.loading = true
        const promises = [
          this.loadEvents()
        ]

        if (withMailTemplates && this.hasMailTemplateAccess) {
          promises.push(this.loadMailTemplates())
        }

        if (this.hasBonusToolsAccess) {
          promises.push(this.loadBenefitOptions())
        }

        try {
          await Promise.all(promises)
        } catch {
        } finally {
          this.loading = false
        }
      },

      loadAvailableProjects () {
        this.isLoadingEntities = true

        return API.call(ENTITY_LIST)
          .then(r => this.entities = r.data)
          .finally(() => this.isLoadingEntities = false)
      },

      onLoadAuditLog(event) {
        this.isLoadingAuditLog = true

        return API.call('/$v/notification-center/events/audit_log.json', {
          event_id: event.event_id,
        })
          .then(r => {
            this.auditLog = r.data
            this.auditLogTarget = event
          })
          .finally(() => this.isLoadingAuditLog = false)
      },

      onCopyNotification (notification) {
        this.isCopyingNotification = true
        this.notificationToCopy = notification
      },

      getCopyTargetEntities () {
        if (!this.isCopyingNotification) {
          return []
        }

        return this.entities.filter(({ value }) => value !== this.notificationToCopy.entity_id)
      },

      async onConfirmCopy () {
        const yes = await this.$root.$confirm('Confirm', 'Are you sure you want to copy this template?')

        if (!yes) {
          return
        }

        const copiedEvents = this.copyTargetEntities.map(entityId => ({
          name: `Copied - ${this.notificationToCopy.name}`,
          entity_id: entityId,
          trigger: this.notificationToCopy.trigger,
          status: 'disabled',
          template: {
            name: this.notificationToCopy.template.name,
            base: this.notificationToCopy.template.base,
            popup: this.notificationToCopy.template.popup,
            toast: this.notificationToCopy.template.toast,
            url: this.notificationToCopy.template.url,
          },
          rules: this.notificationToCopy.rules
        }))

        this.loading = true
        for (let i = 0; i < copiedEvents.length; i++) {
          const event = copiedEvents[i]
          try {
            await API.call('/$v/notification-center/events/update.json', {
              event,
            })
          } catch {
            this.$showError(`Error copying ${this.notificationToCopy.name} template for project ${i + 1} of ${copiedEvents.length}`)
          }
        }

        this.loading = false
        this.isCopyingNotification = false
        this.$showSuccess(`Successfully copied ${this.notificationToCopy.name}`)
      },
    }
  }
</script>

<style lang="scss" scoped>
  $primary-color: #4F4F4F;
  $primary-font-size: 14px;
  $weight-bold: 600;

  .notification-center {
    padding-right: 0px;

    &__wrapper {
      width: 100%;
      //height: calc(100vh - 170px);
      overflow: auto;
      padding-right: 5px;
    }

    &__title {
      font-size: 20px;
      line-height: 40px;
      font-weight: 400;
    }

    &__expansion {
      display: flex;
      flex-direction: column;
      gap: 16px;

      &-panel {
        height: 56px;

        &-header {
          height: 56px;
          padding: 12px 16px 10px 10px;

          &-title {
            font-weight: $weight-bold;
            font-size: 16px;
            color: #333333;
            width: 15%;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          &-active {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 2px;
            font-size: 12px;
            color: $primary-color;
            width: 130px;

            &-badge {
              padding-top: 2px;

              &::v-deep .v-badge__badge {
                min-width: 10px;
                aspect-ratio: 1/1;
                height: auto;
              }
            }

            &-switch {
              margin: 0px 0px 0px 10px;
            }
          }

          &-type {
            font-size: $primary-font-size;
            display: flex;
            align-items: center;
            gap: 4px;
            color: $primary-color;
            width: 120px;
            text-overflow: ellipsis;
            overflow: hidden;

            &-text {
              font-weight: $weight-bold;
            }
          }

          &-date {
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 8px;
            color: $primary-color;

            > span:first-child {
              font-weight: $weight-bold;
            }
          }

          &-btn::v-deep {
            font-weight: 500;
            font-size: 13px;
            min-width: 50px;
            width: 48px;
            height: 32px;
            padding: 6px 10px;
          }
        }

        &-wrapper-content {
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }

        &-content {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: 7px;
          margin-top: 16px;

          &-section {
            flex: 1;
            display: flex;
            gap: 14px;
            font-size: $primary-font-size;
            color: $primary-color;


            &-title {
              width: 80px;
              text-align: end;
              font-weight: $weight-bold;
              padding: 12px 0;
            }

            &-field {
              background-color: #F2F2F2;
              border-radius: 4px;
              width: 100%;
              padding: 10px;

              & > a {
                color: $primary-color;
              }
            }
          }
        }
      }
    }

    &__sort {
      width: 100%;

      &-wrapper {
        height: 100%;
        display: flex;
        justify-content: space-between;
        gap: 20px;
        color: $primary-color;
        font-size: $primary-font-size;

        &-sortby {
          & ::v-deep .v-select__selections {
            line-height: 28px;
          }
        }
      }

      &-item {
        height: 48px;
        display: flex;
        align-items: center;

        &-group {
          & ::v-deep .v-text-field {
            padding: 0;
            margin: 0;
          }

          & ::v-deep .v-select {
            .v-input__slot {
              height: 36px;
            }

            .v-chip {
              margin-top: 2px;
              margin-bottom: 4px;
            }
          }
        }

        @media (max-width: 1024px) {
          &-content {
            width: 180px;
          }
        }
      }

      &-item > span:first-child {
        margin-right: 8px;
      }
    }
  }


</style>

